/**
 * Provider Relationship PDF Generator
 *
 * Generates a PDF for the provider "Relationship" tab
 * (src/providers/ProviderCompare.js): the filter summary, the stat tiles, then
 * the charts two to a row, drawn as vectors.
 *
 * Reuses the existing Doctivity gradient-header template
 * (drawPageHeader / drawContinuationPage / loadDoctivityLogo).
 *
 * The caller (component) supplies a fully-resolved render model — labels,
 * series, colors, formatted periods — so this module stays pure drawing and
 * the PDF can never disagree with what is on screen.
 */

import { jsPDF } from 'jspdf';
import { format } from 'date-fns';
import { registerRobotoFont } from './robotoFont';
import { drawPageHeader, drawContinuationPage } from './chartRenderer';
import { loadDoctivityLogo } from './logoManager';

// ─── Layout constants ─────────────────────────────────────────────────────────
const LOGO_MAX_W = 90;
const LOGO_MAX_H = 22;
const LOGO_ASPECT = 261 / 59;
const CONTENT_L = 24;
const CONTENT_R = 24;
const FOOTER_RESERVE = 40;
const GUTTER = 16;
const TILE_H = 72;
const CHART_H = 290;
const LEGEND_ROW_H = 11;

const TITLE_TXT = [33, 37, 41]; // #212529
const SUB_TXT = [102, 102, 102]; // #666666
const MUTED_TXT = [142, 142, 142]; // #8E8E8E
const GRID = [226, 232, 240]; // #E2E8F0
const BORDER = [222, 226, 230];
const UP = [2, 160, 110];
const DOWN = [220, 80, 80];

/** #RRGGBB → [r,g,b]; falls back to grey on bad input. */
const hexToRgb = (hex) => {
  if (typeof hex === 'string') {
    const m = hex.replace('#', '');
    if (m.length === 6) {
      const n = parseInt(m, 16);
      if (!Number.isNaN(n)) return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
  }
  return MUTED_TXT;
};

/** Place the cached logo at the top-right corner of the current page. */
const placeLogoOnPage = (doc, logo, pageWidth) => {
  if (!logo) return;
  let w = LOGO_MAX_W;
  let h = w / LOGO_ASPECT;
  if (h > LOGO_MAX_H) { h = LOGO_MAX_H; w = h * LOGO_ASPECT; }
  const lx = pageWidth - 15 - LOGO_MAX_W;
  doc.addImage(
    `data:image/${logo.type.toLowerCase()};base64,${logo.data}`,
    logo.type,
    lx, 5, w, h,
  );
};

/** Ellipsize `text` to fit `maxW` points at the current font settings. */
const fit = (doc, text, maxW) => {
  let s = String(text ?? '');
  if (doc.getTextWidth(s) <= maxW) return s;
  while (s.length > 1 && doc.getTextWidth(`${s}…`) > maxW) s = s.slice(0, -1);
  return `${s}…`;
};

/** Round an axis maximum up to a clean step so ticks read well. */
const NICE_STEPS = [1, 1.2, 1.6, 2, 2.4, 3, 4, 5, 6, 8, 10];
const niceMax = (value) => {
  if (!(value > 0)) return 1;
  const pow = 10 ** Math.floor(Math.log10(value));
  return NICE_STEPS.find((step) => step * pow >= value) * pow;
};

const fmt = (value) => Math.round(value).toLocaleString();

/** Card frame + title, period label and one-line note. Returns the body top. */
const drawCard = (doc, chart, x, y, w, h) => {
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, w, h, 3, 3, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...TITLE_TXT);
  const title = fit(doc, chart.title, w * 0.6);
  doc.text(title, x + 10, y + 16);
  const titleW = doc.getTextWidth(title);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED_TXT);
  if (chart.rightLabel) {
    // the right label gets whatever the title leaves, so a short title never
    // forces a long service line name to be cut off
    doc.text(fit(doc, chart.rightLabel, w - 32 - titleW), x + w - 10, y + 16, {
      align: 'right',
    });
  }
  if (chart.note) {
    doc.text(fit(doc, chart.note, w - 20), x + 10, y + 28);
  }
  return y + 38;
};

/** Wrapped color-swatch legend along the bottom of a card. Returns its height. */
const legendHeight = (doc, series, w) => {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  let rows = 1;
  let cx = 0;
  series.forEach((s) => {
    const itemW = 12 + doc.getTextWidth(s.name) + 10;
    if (cx > 0 && cx + itemW > w) { rows++; cx = 0; }
    cx += itemW;
  });
  return rows * LEGEND_ROW_H;
};

const drawLegend = (doc, series, x, y, w) => {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...SUB_TXT);
  let cx = 0;
  let cy = y;
  series.forEach((s) => {
    const itemW = 12 + doc.getTextWidth(s.name) + 10;
    if (cx > 0 && cx + itemW > w) { cy += LEGEND_ROW_H; cx = 0; }
    doc.setFillColor(...hexToRgb(s.color));
    doc.rect(x + cx, cy - 5.5, 7, 6, 'F');
    doc.text(s.name, x + cx + 10, cy);
    cx += itemW;
  });
};

const drawEmpty = (doc, text, x, y, w, h) => {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED_TXT);
  doc.text(text || 'No data.', x + w / 2, y + h / 2, { align: 'center' });
};

/**
 * chart: { categories, xTitle, markIndex?, markLabel?,
 *          series: [{ name, color, data: (number|null)[] }] }
 */
const drawLineChart = (doc, chart, x, y, w, h) => {
  const series = chart.series || [];
  const values = series.flatMap((s) => s.data).filter((v) => v != null);
  if (values.length === 0) {
    drawEmpty(doc, chart.emptyText, x, y, w, h);
    return;
  }
  const legendH = legendHeight(doc, series, w - 20);
  const plotL = x + 42;
  const plotR = x + w - 26; // half of the last x label hangs past the plot
  const plotT = y + 4;
  const plotB = y + h - legendH - 34;
  const max = niceMax(Math.max(...values));
  const count = chart.categories.length;
  const xAt = (i) =>
    count > 1 ? plotL + (i / (count - 1)) * (plotR - plotL) : (plotL + plotR) / 2;
  const yAt = (v) => plotB - (v / max) * (plotB - plotT);

  // y grid + ticks
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...MUTED_TXT);
  doc.setDrawColor(...GRID);
  doc.setLineWidth(0.5);
  for (let t = 0; t <= 4; t++) {
    const v = (max / 4) * t;
    doc.line(plotL, yAt(v), plotR, yAt(v));
    doc.text(fmt(v), plotL - 4, yAt(v) + 2, { align: 'right' });
  }
  // x labels: thin them out so they never collide
  const every = Math.max(1, Math.ceil(count / 7));
  chart.categories.forEach((label, i) => {
    if ((count - 1 - i) % every !== 0) return;
    doc.text(label, xAt(i), plotB + 10, { align: 'center' });
  });
  if (chart.xTitle) {
    doc.text(chart.xTitle, (plotL + plotR) / 2, plotB + 20, { align: 'center' });
  }

  // dashed marker on the period the snapshot cards show
  if (chart.markIndex != null && chart.markIndex >= 0 && chart.markIndex < count) {
    const mx = xAt(chart.markIndex);
    doc.setDrawColor(...MUTED_TXT);
    doc.setLineWidth(0.7);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(mx, plotT, mx, plotB);
    doc.setLineDashPattern([], 0);
    if (chart.markLabel) {
      doc.setFontSize(6.5);
      doc.setTextColor(...SUB_TXT);
      const rightHalf = chart.markIndex > (count - 1) / 2;
      doc.text(chart.markLabel, rightHalf ? mx - 3 : mx + 3, plotT + 7, {
        align: rightHalf ? 'right' : 'left',
      });
    }
  }

  series.forEach((s) => {
    const rgb = hexToRgb(s.color);
    doc.setDrawColor(...rgb);
    doc.setFillColor(...rgb);
    doc.setLineWidth(1.4);
    let prev = null;
    s.data.forEach((v, i) => {
      if (v == null) { prev = null; return; } // a data gap breaks the line
      const point = { x: xAt(i), y: yAt(v) };
      if (prev) doc.line(prev.x, prev.y, point.x, point.y);
      doc.circle(point.x, point.y, 1.6, 'F');
      prev = point;
    });
  });

  drawLegend(doc, series, x + 10, y + h - legendH - 2, w - 20);
};

/** chart: { series: [{ name, color }], rows: [{ name, values: number[] }] } */
const drawBarChart = (doc, chart, x, y, w, h) => {
  const series = chart.series || [];
  const rows = chart.rows || [];
  if (rows.length === 0) {
    drawEmpty(doc, chart.emptyText, x, y, w, h);
    return;
  }
  const legendH = legendHeight(doc, series, w - 20);
  const labelW = Math.min(120, w * 0.34);
  const plotL = x + 10 + labelW;
  const plotR = x + w - 40; // room for the value label after the longest bar
  const plotT = y + 2;
  const plotB = y + h - legendH - 12;
  const max = niceMax(Math.max(...rows.flatMap((r) => r.values)));
  const rowH = Math.min(30, (plotB - plotT) / rows.length);
  const barH = Math.min(9, (rowH - 4) / series.length);

  rows.forEach((row, ri) => {
    const top = plotT + ri * rowH;
    const blockH = barH * series.length;
    const blockT = top + (rowH - blockH) / 2;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...TITLE_TXT);
    doc.text(fit(doc, row.name, labelW - 6), plotL - 6, top + rowH / 2 + 2.5, {
      align: 'right',
    });
    row.values.forEach((value, si) => {
      const barY = blockT + si * barH;
      const barW = value > 0 ? Math.max(1, (value / max) * (plotR - plotL)) : 0;
      if (barW > 0) {
        doc.setFillColor(...hexToRgb(series[si].color));
        doc.rect(plotL, barY, barW, barH - 1, 'F');
      }
      // every slot gets its number, so an empty slot reads as 0, not missing
      doc.setFontSize(6);
      doc.setTextColor(...SUB_TXT);
      doc.text(fmt(value), plotL + barW + 3, barY + (barH - 1) / 2 + 2);
    });
  });
  doc.setDrawColor(...GRID);
  doc.setLineWidth(0.5);
  doc.line(plotL, plotT, plotL, plotT + rows.length * rowH);

  drawLegend(doc, series, x + 10, y + h - legendH - 2, w - 20);
};

/** tile: { label, detail, period, value, delta, deltaDirection, color } */
const drawTile = (doc, tile, x, y, w) => {
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, w, TILE_H, 3, 3, 'S');
  doc.setFillColor(...hexToRgb(tile.color));
  doc.rect(x, y + 4, 3, TILE_H - 8, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...hexToRgb(tile.color));
  doc.text(tile.value == null ? '—' : fmt(tile.value), x + 12, y + 22);

  if (tile.delta) {
    let color = MUTED_TXT;
    if (tile.deltaDirection > 0) color = UP;
    if (tile.deltaDirection < 0) color = DOWN;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...color);
    doc.text(fit(doc, tile.delta, w - 90), x + w - 8, y + 20, { align: 'right' });
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...SUB_TXT);
  // the label names both providers and a direction, so it may take two lines
  const label = doc.splitTextToSize(String(tile.label || ''), w - 22).slice(0, 2);
  [...label, tile.detail, tile.period].filter(Boolean).forEach((line, i) => {
    doc.text(fit(doc, line, w - 22), x + 12, y + 34 + i * 9);
  });
};

/**
 * Build the jsPDF document for the provider relationship view.
 *
 * @param {Object}   opts
 * @param {string}   opts.title
 * @param {string}   [opts.subtitle]
 * @param {string[]} [opts.filters] - one "Label: value" string per filter
 * @param {Array}    [opts.tiles]   - see drawTile
 * @param {Array}    [opts.charts]  - each { type: 'line'|'bar', title,
 *   rightLabel, note, emptyText, fullWidth, ...drawLineChart/drawBarChart model }
 * @returns {Promise<jsPDF>}
 */
export const generateRelationshipPDF = async ({
  title,
  subtitle = '',
  filters = [],
  tiles = [],
  charts = [],
}) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  registerRobotoFont(doc);

  const pageWidth = doc.internal.pageSize.width; // 792
  const pageHeight = doc.internal.pageSize.height; // 612
  const contentW = pageWidth - CONTENT_L - CONTENT_R;

  const logo = await loadDoctivityLogo();
  const placeLogo = () => placeLogoOnPage(doc, logo, pageWidth);

  const { y: contentY } = drawPageHeader(
    doc,
    title || 'Provider Relationship',
    subtitle,
    [],
    { pdfType: 'printable' },
  );
  placeLogo();
  let y = contentY;

  // ── Filter summary ─────────────────────────────────────────────────────────
  if (filters.length > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...SUB_TXT);
    doc.splitTextToSize(filters.join('   |   '), contentW).forEach((line) => {
      doc.text(line, CONTENT_L, y + 8);
      y += 11;
    });
    y += 6;
  }

  // ── Stat tiles ─────────────────────────────────────────────────────────────
  if (tiles.length > 0) {
    const tileW = (contentW - GUTTER * (tiles.length - 1)) / tiles.length;
    tiles.forEach((tile, i) => {
      drawTile(doc, tile, CONTENT_L + i * (tileW + GUTTER), y, tileW);
    });
    y += TILE_H + GUTTER;
  }

  // ── Charts, two to a row ───────────────────────────────────────────────────
  const halfW = (contentW - GUTTER) / 2;
  let column = 0;
  charts.forEach((chart) => {
    const full = !!chart.fullWidth;
    if (full && column === 1) { column = 0; y += CHART_H + GUTTER; }
    if (column === 0 && y + CHART_H > pageHeight - FOOTER_RESERVE) {
      doc.addPage();
      // match page 1's footer (the continuation default is larger and brighter)
      const next = drawContinuationPage(doc, {
        pdfType: 'printable',
        footerFontSize: 7,
        footerTextColor: [220, 230, 255],
      });
      y = typeof next === 'number' ? next : next.y;
      placeLogo();
    }
    const w = full ? contentW : halfW;
    const x = CONTENT_L + (full ? 0 : column * (halfW + GUTTER));
    const bodyY = drawCard(doc, chart, x, y, w, CHART_H);
    const bodyH = y + CHART_H - bodyY - 6;
    if (chart.type === 'line') drawLineChart(doc, chart, x, bodyY, w, bodyH);
    else drawBarChart(doc, chart, x, bodyY, w, bodyH);

    if (full || column === 1) { column = 0; y += CHART_H + GUTTER; }
    else column = 1;
  });

  return doc;
};

/**
 * Generate the PDF and trigger a browser download.
 */
export const downloadRelationshipPDF = async ({
  filenameBase = 'relationship',
  filename,
  ...config
}) => {
  const doc = await generateRelationshipPDF(config);
  if (filename) {
    doc.save(filename);
    return;
  }
  const safeName = filenameBase.replace(/[^a-zA-Z0-9]/g, '_');
  doc.save(`doctivity_${safeName}_${format(new Date(), 'yy_MM_dd')}.pdf`);
};

export default {
  generateRelationshipPDF,
  downloadRelationshipPDF,
};
